import React from 'react';
import {
  DynamicDataSheetGrid,
  keyColumn,
  textColumn,
  createTextColumn,
  isoDateColumn,
} from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import './datagrid.css';

/**
 * Spreadsheet-style entry, Y365-skinned.
 *
 * NEW COMPONENT — not in the upstream design system. Built here to its
 * conventions (named export, sibling .d.ts and .prompt.md) so it can be lifted
 * back: a data grid is not onboarding-specific.
 *
 * Why a library rather than hand-built: paste-from-Excel. A departing IT
 * provider already has their asset list in a spreadsheet, and pasting it is far
 * likelier to get completed than typing forty rows. Clipboard handling,
 * keyboard navigation and range selection are the whole reason this component
 * is not a table of inputs.
 *
 * The library is styled entirely through CSS custom properties, so `datagrid.css`
 * maps them onto Y365 tokens instead of overriding its rules.
 */

/* ------------------------------------------------------------------ select */

function SelectCell({ rowData, setRowData, focus, stopEditing, columnData }) {
  const ref = React.useRef(null);

  React.useLayoutEffect(() => {
    if (focus) ref.current?.focus();
    else ref.current?.blur();
  }, [focus]);

  const options = columnData?.options ?? [];
  const known = options.some((o) => o.value === rowData);

  return (
    <select
      ref={ref}
      className={`y365-datagrid-select${rowData && !known ? ' y365-datagrid-cell-invalid' : ''}`}
      tabIndex={-1}
      value={rowData ?? ''}
      onChange={(e) => {
        setRowData(e.target.value || null);
        // Move on after a choice, the way tabbing through a spreadsheet feels.
        setTimeout(() => stopEditing({ nextRow: false }), 0);
      }}
      onBlur={() => stopEditing({ nextRow: false })}
    >
      <option value=""></option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {/* A pasted value we do not recognise stays visible rather than being
          silently blanked — the customer should see what they pasted. */}
      {rowData && !known && <option value={rowData}>{rowData} (not a known option)</option>}
    </select>
  );
}

/**
 * Choice column. Paste matches on label OR value, case-insensitively, because
 * a spreadsheet will say "laptop" and the option is "Laptop".
 */
export function selectColumn(options = []) {
  return {
    component: SelectCell,
    columnData: { options },
    disableKeys: true,
    keepFocus: true,
    deleteValue: () => null,
    copyValue: ({ rowData }) => {
      const match = options.find((o) => o.value === rowData);
      return match ? match.label : (rowData ?? null);
    },
    pasteValue: ({ value }) => {
      const raw = String(value ?? '').trim();
      if (!raw) return null;
      const match = options.find(
        (o) =>
          o.label.toLowerCase() === raw.toLowerCase() ||
          o.value.toLowerCase() === raw.toLowerCase(),
      );
      // Keep an unmatched paste rather than dropping it. The cell flags itself
      // and validation catches it — losing a customer's data silently is worse
      // than showing them something wrong.
      return match ? match.value : raw;
    },
    isCellEmpty: ({ rowData }) => rowData === null || rowData === undefined || rowData === '',
  };
}

/* -------------------------------------------------------------------- grid */

const numberColumn = createTextColumn({
  alignRight: true,
  parseUserInput: (value) => {
    const n = Number(value.trim());
    return value.trim() === '' || Number.isNaN(n) ? null : n;
  },
  formatBlurredInput: (value) => (value === null || value === undefined ? '' : String(value)),
  parsePastedValue: (value) => {
    const n = Number(String(value).replace(/[,$\s]/g, ''));
    return Number.isNaN(n) ? null : n;
  },
});

function buildColumn(spec) {
  const base =
    spec.type === 'choice'
      ? selectColumn(spec.options ?? [])
      : spec.type === 'number'
        ? numberColumn
        : spec.type === 'date'
          ? isoDateColumn
          : textColumn;

  return {
    ...keyColumn(spec.key, base),
    title: spec.label,
    ...(spec.width ? { basis: spec.width, grow: 0 } : { grow: 1 }),
    minWidth: spec.minWidth ?? 120,
  };
}

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 42;
const MIN_ROWS_SHOWN = 4;
const MAX_ROWS_SHOWN = 12;

export function DataGrid({
  columns = [],
  value = [],
  onChange,
  label,
  hint,
  error,
  addLabel = 'Add row',
  disabled = false,
  id,
  style,
}) {
  const reactId = React.useId();
  const gridId = id || reactId;
  const describedById = `${gridId}-desc`;

  const built = React.useMemo(() => columns.map(buildColumn), [columns]);

  // Grow with content, then scroll. A grid that grows without limit pushes the
  // submit button off the page; one fixed short is miserable to paste into.
  const shown = Math.min(Math.max(value.length + 1, MIN_ROWS_SHOWN), MAX_ROWS_SHOWN);
  const height = HEADER_HEIGHT + shown * ROW_HEIGHT;

  const message = error || hint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', ...style }}>
      {label && (
        <span
          id={`${gridId}-label`}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-bold)',
            color: 'var(--text-strong)',
          }}
        >
          {label}
        </span>
      )}

      <div
        id={gridId}
        tabIndex={-1}
        role="group"
        aria-labelledby={label ? `${gridId}-label` : undefined}
        aria-describedby={message ? describedById : undefined}
        className="y365-datagrid"
        style={error ? { borderColor: 'var(--y-danger)' } : undefined}
      >
        <DynamicDataSheetGrid
          value={value}
          onChange={onChange}
          columns={built}
          height={height}
          rowHeight={ROW_HEIGHT}
          headerRowHeight={HEADER_HEIGHT}
          lockRows={disabled}
          disableExpandSelection={false}
          addRowsComponent={disabled ? false : undefined}
tabIndex={0}
        />
      </div>

      {message && (
        <span
          id={describedById}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-xs)',
            color: error ? 'var(--y-danger)' : 'var(--text-muted)',
          }}
        >
          {message}
        </span>
      )}

      {!disabled && (
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Tip: you can paste straight from a spreadsheet. {addLabel} at the bottom to add more.
        </span>
      )}
    </div>
  );
}
